// ─────────────────────────────────────────────────────────────────────────────
// engagementTemplateSuggestions
//
// Pure utilities for bucketing engagement templates into "Suggested for this
// lead" vs "All templates" inside the email composers (Wave 3a).
//
// Core logic mirrors the BE PracticeEngagementTemplateQuerySet.applicable_for()
// in apps/practice/managers.py:
//   • Empty array in any targeting dimension = universal (matches ANY value).
//   • Non-empty array = contains-any (at least one ID / status overlaps).
//   • All three dimensions are AND-ed together.
//
// The "suggested" bucket is the subset that:
//   1. APPLIES to the lead (all three dimensions match), AND
//   2. Has at LEAST ONE dimension non-empty — i.e. the template is specifically
//      targeted, not a generic "Any" catch-all.  (Option A per Wave 3 plan.)
//
// Status-enum hazard
// ──────────────────
// The lead's status lives on lead_treatments[*].status (a LEAD_STATUS_CHOICES
// value), NOT on a top-level leadDetail.status field.  The live serializer
// does NOT surface a reliable top-level status — the source of truth is the
// latest lead_treatment (sorted by updated_at DESC), consistent with the
// app-wide convention in LeadDetailViewBody, LeadDetailTasks, etc.
//
// Template applies_to_statuses uses TemplateLeadStatus (apps/tasks/choices.py).
// These are two SEPARATE enums with one differing value:
//   FAILED_TO_ATTEND_CONSULTATION  →  FAILED_TO_ATTEND
// All other lifecycle values are identical strings.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps lead_treatment.status (LEAD_STATUS_CHOICES) →
 *       template applies_to_statuses value (TemplateLeadStatus).
 *
 * Only one entry differs: FAILED_TO_ATTEND_CONSULTATION → FAILED_TO_ATTEND.
 * All others are identity mappings kept explicit so the full lifecycle is
 * machine-readable here and typos can't go undetected.
 *
 * Fallback: extractLeadCtx passes ?? rawStatus for any unknown value so new
 * statuses degrade gracefully rather than silently dropping suggestions.
 */
export const LEAD_STATUS_TO_TEMPLATE_STATUS = {
    NEW:                            'NEW',
    CONSULTATION_BOOKED:            'CONSULTATION_BOOKED',
    CONSULTATION_ATTENDED:          'CONSULTATION_ATTENDED',
    TREATMENT_STARTED:              'TREATMENT_STARTED',
    FAILED_TO_ATTEND_CONSULTATION:  'FAILED_TO_ATTEND',   // ← THE ONLY NON-IDENTITY MAPPING
    CLOSED:                         'CLOSED',
};

/**
 * Extracts the lead context needed for template suggestion bucketing.
 *
 * Status source:
 *   The lead's current status lives on lead_treatments[*].status, NOT on a
 *   top-level field.  We take the latest treatment by updated_at (same
 *   convention as LeadDetailViewBody, LeadDetailTasks, etc.) and use its
 *   status as the representative status for the whole lead.
 *
 * Treatment ID path:
 *   lead_treatments[*].treatment_data.treatment_item.id          — master TreatmentItem PK
 *   lead_treatments[*].treatment_data.treatment_item.treatment_category.id — TreatmentCategory PK
 *
 * NOTE: treatment_data.id is the PracticeTreatment PK (large, practice-scoped) —
 * that is the WRONG space for matching against applies_to_treatment_items.
 * Only treatment_data.treatment_item.id is the correct master ID.
 *
 * Multiple treatments are unioned (deduplicated). A template targeting ANY ONE
 * of the lead's treatments/categories is considered a match (mirrors BE).
 *
 * @param {Object|null|undefined} leadDetail — lead object from Redux
 * @returns {{ mappedStatus: string|null, categoryIds: number[], itemIds: number[] }}
 */
export function extractLeadCtx(leadDetail) {
    if (!leadDetail?.id) return null;   // no lead identity → null (honours documented contract)

    const treatments = leadDetail?.lead_treatments ?? [];

    // Status: read from the latest treatment (sorted by updated_at DESC).
    // This is the app-wide convention — the top-level leadDetail object does
    // not surface a reliable standalone status field in the live serializer.
    const latest = treatments
        .slice()
        .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
    const rawStatus = latest?.status ?? null;
    const mappedStatus = rawStatus
        ? (LEAD_STATUS_TO_TEMPLATE_STATUS[rawStatus] ?? rawStatus)  // graceful fallback
        : null;

    const categoryIds = [
        ...new Set(
            treatments
                .map((lt) => lt?.treatment_data?.treatment_item?.treatment_category?.id)
                .filter(Boolean)
        ),
    ];

    const itemIds = [
        ...new Set(
            treatments
                .map((lt) => lt?.treatment_data?.treatment_item?.id)
                .filter(Boolean)
        ),
    ];

    return { mappedStatus, categoryIds, itemIds };
}

/**
 * Buckets a flat template list into "suggested" (specifically targeted AND
 * applicable) vs the full list.
 *
 * Mirrors BE applicable_for() exactly:
 *   - Empty targeting array = universal (pass-through for that dimension).
 *   - Non-empty = contains-any intersection with lead context.
 *   - AND across all three dimensions.
 *
 * "Suggested" = applies AND hasTargeting (Option A):
 *   Generic "Any" templates (all three arrays empty) are EXCLUDED from
 *   suggested — they belong in "All templates" only.
 *
 * @param {Array}       templates  — flat list (templateDetailsList / subjectTemplateList)
 * @param {Object|null} leadCtx    — from extractLeadCtx(); null = no lead present
 * @returns {{ suggested: Array, all: Array }}
 *   suggested — templates that are specifically targeted at this lead
 *   all       — full list unchanged (used for the "All templates" section)
 */
export function computeSuggestedTemplates(templates, leadCtx) {
    if (!leadCtx || !Array.isArray(templates)) {
        return { suggested: [], all: templates ?? [] };
    }

    const { mappedStatus, categoryIds, itemIds } = leadCtx;

    const suggested = templates.filter((t) => {
        const statusList   = t?.applies_to_statuses              ?? [];
        const categoryList = t?.applies_to_treatment_categories  ?? [];
        const itemList     = t?.applies_to_treatment_items        ?? [];

        // Universal check: a template with all three empty is a catch-all —
        // it applies but has NO targeting, so it should NOT appear in suggested.
        const hasTargeting =
            statusList.length > 0 || categoryList.length > 0 || itemList.length > 0;

        if (!hasTargeting) return false;

        // Dimension match (empty array = universal = always passes)
        const statusMatch =
            statusList.length === 0 ||
            (mappedStatus != null && statusList.includes(mappedStatus));

        const categoryMatch =
            categoryList.length === 0 ||
            categoryIds.some((id) => categoryList.includes(id));

        const itemMatch =
            itemList.length === 0 ||
            itemIds.some((id) => itemList.includes(id));

        return statusMatch && categoryMatch && itemMatch;
    });

    return { suggested, all: templates };
}
